/**
 * CRMSettings Component
 *
 * Generic, multi-tenant CRM settings panel. Each tenant chooses a CRM provider:
 *   - 'builtin': a tenant's built-in CRM, configured server-side
 *   - 'zoho': legacy Zoho CRM, configured via the Zoho OAuth panel (surfaced only when selected)
 *
 * Includes a note about the migration from Zoho to the built-in CRM so users understand
 * why the Zoho-specific configuration is now hidden by default.
 */
import { useEffect, useState } from 'react';
import { authFetch } from '../api/orchestrator';
import { ZohoCRM } from './ZohoCRM';

type CrmProvider = 'builtin' | 'zoho';

export function CRMSettings() {
  const [provider, setProvider] = useState<CrmProvider>('builtin');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch('/api/admin/crm-provider');
        const data = await res.json();
        if (data.ok && data.provider) setProvider(data.provider);
      } catch (e) {
        setError('Failed to load CRM provider setting.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const changeProvider = async (next: CrmProvider) => {
    const previous = provider;
    setProvider(next);
    setSaving(true);
    setError(null);
    try {
      const res = await authFetch('/api/admin/crm-provider', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider: next }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Save failed');
    } catch (e) {
      setProvider(previous); // revert on failure
      setError('Failed to save CRM provider. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ padding: 20 }}><p>Loading CRM settings…</p></div>;
  }

  return (
    <div style={{ padding: '4px 0' }}>
      <h2 className="gca-title">🏢 CRM Integration</h2>

      {/* Migration note */}
      <div
        style={{
          background: '#eef6ff',
          border: '1px solid #b6d8ff',
          borderRadius: 8,
          padding: '12px 16px',
          margin: '12px 0 20px',
          color: '#1a4971',
          fontSize: 14,
          lineHeight: 1.5,
        }}
      >
        <strong>ℹ️ We migrated from Zoho CRM to our built-in CRM (July 2026).</strong>
        <br />
        Lead lookups and response control now use the built-in CRM by default — no setup
        required. If your team still uses Zoho (or another CRM), choose it below to reveal
        its configuration.
      </div>

      {/* Provider selector */}
      <div style={{ marginBottom: 20 }}>
        <label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>
          CRM Provider
        </label>
        <select
          value={provider}
          disabled={saving}
          onChange={(e) => changeProvider(e.target.value as CrmProvider)}
          style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ccc', minWidth: 260 }}
        >
          <option value="builtin">Built-in CRM (default)</option>
          <option value="zoho">Zoho CRM</option>
        </select>
        {saving && <span style={{ marginLeft: 10, color: '#888' }}>Saving…</span>}
        {error && <div style={{ color: '#c0392b', marginTop: 8 }}>{error}</div>}
      </div>

      {/* Provider-specific configuration */}
      {provider === 'builtin' ? (
        <div
          style={{
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            padding: '16px',
            background: '#fafafa',
          }}
        >
          <h3 style={{ marginTop: 0, marginBottom: 8 }}>Built-in CRM</h3>
          <p style={{ margin: 0, color: '#555', fontSize: 14, lineHeight: 1.5 }}>
            Connected to the built-in CRM. Authentication is managed on the server
            (no credentials to enter here). Lead-by-phone lookups and lead-status based
            response control are served by the built-in CRM automatically.
          </p>
        </div>
      ) : (
        <ZohoCRM />
      )}
    </div>
  );
}

export default CRMSettings;
